import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Download, Copy, Wand2, FileText, CheckCircle, User, Trash2 } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../config/api';

import { clearStoredCVInfo } from '../utils/cvDataExtractor';
import type { FinalResults } from '../types';
import ErrorMessage from './ErrorMessage';
import { useAuth } from '../hooks/useAuth';

interface CoverLetterGeneratorProps {
  analysisResults: FinalResults;
  onClose: () => void;
  outputId : string
}

const CoverLetterGenerator: React.FC<CoverLetterGeneratorProps> = ({
  analysisResults,
  onClose,
  outputId
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedLetter, setGeneratedLetter] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    candidateName: '',
    candidateEmail: '',
    candidatePhone: '',
    companyName: '',
    positionTitle: '',
    hiringManagerName: '',
    tone: 'professional' as 'professional' | 'enthusiastic' | 'creative'
  });
  const [copied, setCopied] = useState(false);
  const [hasStoredInfo, setHasStoredInfo] = useState(false);
  const { t,i18n } = useTranslation();
  const language = i18n.language
  const { session } = useAuth();
  // Charger les informations sauvegardées au montage du composant
  useEffect(() => {
    // const storedInfo = getStoredCVInfo();
    if (analysisResults?.userInfo?.name || analysisResults?.userInfo?.email || analysisResults?.userInfo?.phone) {
      setFormData(prev => ({
        ...prev,
        candidateName: analysisResults?.userInfo?.name || prev.candidateName,
        candidateEmail: analysisResults?.userInfo?.email || prev.candidateEmail,
        candidatePhone: analysisResults?.userInfo?.phone || prev.candidatePhone,
        companyName: analysisResults?.userInfo?.companyName || prev.companyName,
        positionTitle: analysisResults?.userInfo?.positionTitle || prev.positionTitle
      }));
      setHasStoredInfo(true);
    }
  }, []);
  const showMessage = (message: string, type: "error" | "success") => {
    if (type === "error") {
      setErrorMessage(message);
      setTimeout(() => setErrorMessage(null), 5000);
    } else {
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  };


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const clearStoredInfo = () => {
    clearStoredCVInfo();
    setFormData(prev => ({
      ...prev,
      candidateName: '',
      candidateEmail: '',
      candidatePhone: '',
      companyName: '',
      positionTitle: ''
    }));
    setHasStoredInfo(false);
  };

  const generateCoverLetter = async () => {
    try {
      setIsGenerating(true);
      
      // Simuler la génération avec un délai
      // await new Promise(resolve => setTimeout(resolve, 3000));
      const bodyData = {
        outputId : outputId,
        userInfo : {
          name : analysisResults.userInfo?.name,
          email : analysisResults.userInfo?.email,
          phone : analysisResults.userInfo?.phone,
          companyName : formData.companyName,
          positionTitle : formData.positionTitle,
          hiringManagerName : formData.hiringManagerName,
          tone : formData.tone
        },        
        language : language === 'fr' ? 'french' : 'english'
      }
      // console.log("BODY : ",bodyData);
      
      const response = await axios.post(`${API_BASE_URL}/cv-match/cover-letter`,bodyData,{
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        }
      })
      // console.log("ReSpOnse : ",response)

      if(response.data.status == 'success' && response.data.code == 200){
        setGeneratedLetter(response.data?.coverLetterContent?.coverLetter)
      }
    } catch (error) {
        showMessage(
          `${t("analyzer.analysis_failed")}: ${
            error instanceof Error && typeof (error as any).response?.data?.error === "string"
              ? (error as any).response.data.error
              : "Unknown error"
          }`,
          "error"
        );
    }finally{
      setHasStoredInfo(true);    
      setIsGenerating(false);
    }
    // if(response?.code === '')
    // const letter = createPersonalizedCoverLetter(formData, analysisResults);
    // setGeneratedLetter(letter);
    
    // Save form data
    // localStorage.setItem('coverLetterInfo', JSON.stringify(formData));
  };

  // const createPersonalizedCoverLetter = (data: typeof formData, results: FinalResults): string => {
  //   const toneTexts = {
  //     professional: t('cover_letter.tones.professional'),
  //     enthusiastic: t('cover_letter.tones.enthusiastic'),
  //     creative: t('cover_letter.tones.creative')
  //   };

  //   const experience = results.analysisResults.experience;
  //   const skills = results.analysisResults.skills?.found || [];
  //   const topSkills = skills.slice(0, 3).map(s => s.found);
  //   const score = results.globalScore;

  //   return t('cover_letter.template', {
  //     candidateName: data.candidateName,
  //     candidateEmail: data.candidateEmail,
  //     candidatePhone: data.candidatePhone,
  //     companyName: data.companyName,
  //     positionTitle: data.positionTitle,
  //     hiringManagerName: data.hiringManagerName || t('cover_letter.dear_hiring_manager'),
  //     tone: toneTexts[data.tone as keyof typeof toneTexts],
  //     score: score,
  //     skills: topSkills.join(', '),
  //     experience: experience?.relevantExperiences && experience.relevantExperiences.length > 0 ? experience.relevantExperiences[0]?.summary.title || t('cover_letter.experience.default') : t('cover_letter.experience.default'),
  //     experienceDetails: experience?.relevantExperiences && experience.relevantExperiences.length > 0 ? experience.relevantExperiences[0]?.summary.details || t('cover_letter.experience.default_details') : t('cover_letter.experience.default_details'),
  //     date: new Date().toLocaleDateString()
  //   });
  // };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLetter);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(t('cover_letter.copy_error'), err);
    }
  };

  const downloadAsText = () => {
    const blob = new Blob([generatedLetter], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${t('cover_letter.filename')}_${formData.positionTitle.replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between relative">
          <div className="flex items-center">
            <FileText className="w-6 h-6 text-indigo-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('cover_letter.title')}</h2>
              <p className="text-gray-600 mt-1">{t('cover_letter.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors absolute sm:relative top-[5px] right-[5px] sm:top-[unset] sm:right-[unset]"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {!generatedLetter ? (
            <div className="space-y-6">
              {hasStoredInfo && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start">
                      <User className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-green-900 mb-1">{t('cover_letter.stored_info')}</h4>
                        <p className="text-sm text-green-800">
                          {t('cover_letter.stored_info_details')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={clearStoredInfo}
                      className="flex items-center text-sm text-green-700 hover:text-green-900 font-medium"
                      title={t('cover_letter.clear_stored_info')}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      {t('cover_letter.clear')}
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('cover_letter.candidate_name')} *
                    {hasStoredInfo && analysisResults?.userInfo?.name && (
                      <span className="text-green-600 text-xs ml-2">{t('cover_letter.extracted_from_cv')}</span>
                    )}
                  </label>
                  <input
                    type="text"
                    name="candidateName"
                    value={formData.candidateName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t('cover_letter.candidate_name_placeholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('cover_letter.candidate_email')}
                    {hasStoredInfo && analysisResults?.userInfo?.email && (
                      <span className="text-green-600 text-xs ml-2">{t('cover_letter.extracted_from_cv')}</span>
                    )}
                  </label>
                  <input
                    type="email"
                    name="candidateEmail"
                    value={formData.candidateEmail}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t('cover_letter.candidate_email_placeholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('cover_letter.candidate_phone')}
                    {hasStoredInfo && analysisResults?.userInfo.phone && (
                      <span className="text-green-600 text-xs ml-2">{t('cover_letter.extracted_from_cv')}</span>
                    )}
                  </label>
                  <input
                    type="tel"
                    name="candidatePhone"
                    value={formData.candidatePhone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t('cover_letter.candidate_phone_placeholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('cover_letter.company_name')} *
                    {hasStoredInfo && analysisResults?.userInfo?.companyName && (
                      <span className="text-green-600 text-xs ml-2">{t('cover_letter.extracted_from_cv')}</span>
                    )}
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t('cover_letter.company_name_placeholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('cover_letter.position_title')} *
                    {hasStoredInfo && analysisResults?.userInfo?.positionTitle && (
                      <span className="text-green-600 text-xs ml-2">{t('cover_letter.extracted_from_cv')}</span>
                    )}
                  </label>
                  <input
                    type="text"
                    name="positionTitle"
                    value={formData.positionTitle}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t('cover_letter.position_title_placeholder')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('cover_letter.hiring_manager_name')}
                  </label>
                  <input
                    type="text"
                    name="hiringManagerName"
                    value={formData.hiringManagerName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t('cover_letter.hiring_manager_name_placeholder')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('cover_letter.letter_tone')}
                </label>
                <select
                  name="tone"
                  value={formData.tone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="professional">{t('cover_letter.tones.professional')}</option>
                  <option value="enthusiastic">{t('cover_letter.tones.enthusiastic')}</option>
                  <option value="creative">{t('cover_letter.tones.creative')}</option>
                </select>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-start">
                  <div className="text-blue-500 mr-3 mt-1">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">{t('cover_letter.intelligent_generation')}</h4>
                    <p className="text-sm text-blue-800">
                      {t('cover_letter.intelligent_generation_details', { score: analysisResults.globalScore })}
                      {hasStoredInfo && (
                        <span className="block mt-1 font-medium">
                          💡 {t('cover_letter.stored_info_details')}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              {errorMessage && (
                <div className="w-full">
                  <ErrorMessage message={errorMessage} />
                </div>
              )}
              <button
                onClick={generateCoverLetter}
                disabled={!formData.candidateName || !formData.companyName || !formData.positionTitle || isGenerating}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
              >
                {isGenerating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {t('cover_letter.generating')}
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5 mr-2" />
                    {t('cover_letter.generate_letter')}
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">{t('cover_letter.generated_letter')}</h3>
                <div className="flex space-x-3">
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                        {t('cover_letter.copied')}
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        {t('cover_letter.copy')}
                      </>
                    )}
                  </button>
                  <button
                    onClick={downloadAsText}
                    className="flex items-center px-4 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('cover_letter.download')}
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">
                  {generatedLetter}
                </pre>
              </div>

              <div className="flex justify-end">
                {/* <button
                  onClick={() => {
                    setGeneratedLetter('');
                    // Ne pas réinitialiser les informations personnelles si elles sont sauvegardées
                    if (!hasStoredInfo) {
                      setFormData({
                        candidateName: '',
                        candidateEmail: '',
                        candidatePhone: '',
                        companyName: '',
                        positionTitle: '',
                        hiringManagerName: '',
                        tone: 'professional'
                      });
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        companyName: '',
                        positionTitle: '',
                        hiringManagerName: '',
                        tone: 'professional'
                      }));
                    }
                  }}
                  className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  {t('cover_letter.generate_new_letter')}
                </button> */}
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                >
                  {t('cover_letter.close')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoverLetterGenerator;